import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Compass, Flame, Sparkles, Map, BookOpen, Activity, Bookmark, X as XIcon, HelpCircle, GraduationCap, Bell, AlertCircle, RotateCcw, TrendingUp, TrendingDown, Target, ShieldCheck, Check, Plus, ArrowRight, NotebookPen, Rocket, Share2, MessageCircle, CheckCircle2, Circle, Lightbulb, Quote } from "lucide-react";
import client, { detectOrgSlug, fetchCobrand } from "../lib/api";
import { PledgeCard } from "./PledgeCard";
import { MoodPicker, MoodTag } from "../components/MoodPicker";
import { moodTint } from "../components/moods";
import { personalQuoteOfDay } from "../lib/quotes";
import { familyOf, FAMILY_QUOTES } from "../lib/archetypeIdentity";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import { GoalsTab } from "./goals/GoalsTab";
import { RoadmapNudges } from "./roadmap/RoadmapNudges";
import { WeeklyDigest } from "./roadmap/WeeklyDigest";
import NoteNudge from "../components/NoteNudge";
import CheckinPopup from "../components/CheckinPopup";
import { WeeklyPulseCard } from "../components/WeeklyPulse";

/**
 * A time-aware, lightly playful hello. Combines the hour of day with
 * the person's name, archetype, and streak so it feels like Klair
 * actually remembers them. Warm and dry, never try-hard. The daily
 * seed keeps the line stable within a day and fresh the next.
 */
function buildHello({ name, archName, streakDays, now }) {
  const hour = now.getHours();
  const seed = now.getDate();
  const pick = (arr) => arr[seed % arr.length];

  const who = (name || "").trim();
  const archLower = archName ? archName.toLowerCase() : "";
  const archBare = archLower.replace(/^the\s+/, "");

  let bucket, kicker, prefix;
  if (hour >= 22 || hour < 5) {
    bucket = "late"; kicker = "late night";
    prefix = who ? `Up late, ${who}?` : "Up late?";
  } else if (hour < 12) {
    bucket = "morning"; kicker = "morning";
    prefix = who ? `Morning, ${who}.` : "Good morning.";
  } else if (hour < 17) {
    bucket = "afternoon"; kicker = "afternoon";
    prefix = who ? `Afternoon, ${who}.` : "Good afternoon.";
  } else {
    bucket = "evening"; kicker = "evening";
    prefix = who ? `Evening, ${who}.` : "Good evening.";
  }

  const timeQuips = {
    morning: ["Coffee first, then the world.", "A slow start is still a start.", "Fresh day, blank page."],
    afternoon: ["Good time to nudge one thing forward.", "The afternoon is yours to bend.", "Still plenty of runway today."],
    evening: ["Good hour to close a loop or two.", "Wind-down, but I'm still here.", "Evenings are underrated for a small win."],
    late: ["The big ideas love this hour, don't they.", "Night owl mode. I don't judge.", "Late, but who's counting."],
  };

  let quip;
  if (typeof streakDays === "number" && streakDays >= 2) {
    quip = pick([
      `Day ${streakDays} of showing up. I see you.`,
      `That's ${streakDays} days running. I'm impressed.`,
      `${streakDays} days straight. Momentum suits you.`,
    ]);
  } else if (archLower) {
    quip = pick([
      `Still ${archLower}, I see.`,
      `The ${archBare} in you is showing.`,
      ...timeQuips[bucket],
    ]);
  } else {
    quip = pick(timeQuips[bucket]);
  }

  return { kicker, title: `${prefix} ${quip}` };
}

// A plain-English read of the fit/readiness cell, so the dashboard never
// shows a bare number with no meaning.
function fitHint(cell) {
  if (!cell) return "";
  const q = (cell.quadrant_or_cell || "").toLowerCase();
  const fitPart = q.includes("high fit")
    ? "one direction stands out clearly"
    : "you're still weighing a few directions";
  const readyPart = q.includes("growing readiness")
    ? "and you're building up to move on it"
    : "and you're ready to move on it";
  return `${fitPart} ${readyPart}`;
}


// A friendly relative label for a note's day, so the journal reads human.
function formatNoteDay(day) {
  if (!day) return "";
  try {
    const d = new Date(day + "T00:00:00");
    const today = new Date(); today.setHours(0, 0, 0, 0);
    const diff = Math.round((today - d) / 86400000);
    if (diff <= 0) return "today";
    if (diff === 1) return "yesterday";
    if (diff < 7) return `${diff} days ago`;
    return d.toLocaleDateString(undefined, { month: "short", day: "numeric" });
  } catch (_e) { return day; }
}

// Warm, muted tints so each note carries its own colour without the wall
// of cards ever looking noisy. Stable per note via a hash of the id.
const NOTE_TINTS = [
  { bg: "hsl(var(--klareet-teal) / 0.09)", border: "hsl(var(--klareet-teal) / 0.24)", bar: "hsl(var(--klareet-teal))" },
  { bg: "hsl(var(--klareet-amber) / 0.13)", border: "hsl(var(--klareet-amber) / 0.30)", bar: "hsl(var(--klareet-amber))" },
  { bg: "hsl(28 90% 96%)", border: "hsl(28 78% 87%)", bar: "hsl(28 74% 62%)" },
  { bg: "hsl(255 45% 96%)", border: "hsl(255 34% 88%)", bar: "hsl(255 40% 66%)" },
  { bg: "hsl(200 55% 95%)", border: "hsl(200 44% 85%)", bar: "hsl(200 55% 55%)" },
];
function noteTint(id) {
  const h = [...String(id || "")].reduce((a, c) => a + c.charCodeAt(0), 0);
  return NOTE_TINTS[h % NOTE_TINTS.length];
}


/**
 * Home. Post-conversation landing. Shows a warm greeting on the
 * second visit (safe surfaces only, never a check-in state),
 * the honest line, the streak, the read + roadmap jump, and a
 * per-layer movement card.
 */
export default function HomeScreen({ product }) {
  const [report, setReport] = useState(null);
  const [streak, setStreak] = useState({ streak_days: 0, last_days_logged: 0 });
  const [note, setNote] = useState("");
  const [reflectResult, setReflectResult] = useState(null);
  const [makeGoal, setMakeGoal] = useState(false);
  const [mood, setMood] = useState(null);
  const [notes, setNotes] = useState([]);
  const [notesLoaded, setNotesLoaded] = useState(false);
  const [goalAdded, setGoalAdded] = useState(false);
  const [busy, setBusy] = useState(false);
  const [movement, setMovement] = useState(null);
  const [greeting, setGreeting] = useState(null);
  const [rail, setRail] = useState([]);
  const [name, setName] = useState("");
  const [resolved, setResolved] = useState(false);
  const [upcoming, setUpcoming] = useState([]);
  const [college, setCollege] = useState(null);
  const base = product === "grow" ? "/grow" : "";

  const loadRail = () => {
    client.get("/reading-rail")
      .then((r) => setRail(r?.data?.entries || []))
      .catch(() => setRail([]));
  };

  const loadNotes = () => {
    client.get("/engagement/reflection")
      .then((r) => { setNotes(r?.data?.items || []); setNotesLoaded(true); })
      .catch(() => { setNotes([]); setNotesLoaded(true); });
  };

  useEffect(() => {
    const raw = localStorage.getItem("klareet.report");
    if (raw) { try { setReport(JSON.parse(raw)); } catch (_e) { /* ignore */ } }
    client.get(`/demo/read/movement?product=${product}`).then((r) => setMovement(r.data || null)).catch(() => setMovement(null));
    client.get("/accountability/greeting").then((r) => setGreeting(r?.data?.greeting || null)).catch(() => setGreeting(null));
    // Name and streak both feed the time-aware hello, so wait for
    // both to settle before revealing it to avoid a text swap.
    Promise.allSettled([
      client.get("/consent/status"),
      client.get("/engagement/streak"),
    ]).then(([statusRes, streakRes]) => {
      if (statusRes.status === "fulfilled") {
        const f = (statusRes.value?.data?.display_name || "").trim();
        if (f) setName(f.split(/\s+/)[0]);
      }
      if (streakRes.status === "fulfilled" && streakRes.value?.data) {
        setStreak(streakRes.value.data);
      }
      setResolved(true);
    });
    loadRail();
    loadNotes();
    client.get(`/roadmap/upcoming?product=${product}`)
      .then((r) => setUpcoming(r?.data?.items || []))
      .catch(() => setUpcoming([]));
    const slug = detectOrgSlug();
    if (slug) fetchCobrand(slug).then((d) => setCollege(d || null)).catch(() => setCollege(null));
  }, [product]);

  const setRailState = async (id, state) => {
    try {
      const { data } = await client.patch(`/reading-rail/${id}`, { state });
      setRail((prev) => prev.map((e) => (e.id === id ? data : e)));
    } catch (_e) { /* silent */ }
  };

  const removeRail = async (id) => {
    try {
      await client.delete(`/reading-rail/${id}`);
      setRail((prev) => prev.filter((e) => e.id !== id));
    } catch (_e) { /* silent */ }
  };

  const reflect = async () => {
    if (!note.trim()) return;
    setBusy(true);
    setGoalAdded(false);
    try {
      const { data } = await client.post("/engagement/reflection", {
        kind: "daily", text: note.trim(), make_goal: makeGoal, mood: mood,
      });
      setReflectResult(data);
      if (data?.goal) setGoalAdded(true);
      setNote("");
      setMakeGoal(false);
      setMood(null);
      loadNotes();
      const r = await client.get("/engagement/streak");
      setStreak(r.data || streak);
    } catch (_e) {
      setReflectResult({ error: "That did not save. Please try again." });
    } finally {
      setBusy(false);
    }
  };

  const addNoteAsGoal = async (text) => {
    try {
      await client.post("/engagement/goal", { title: text.slice(0, 90), detail: "from a note you wrote" });
      setGoalAdded(true);
      setReflectResult((prev) => (prev ? { ...prev, looks_like_goal: false } : prev));
    } catch (_e) { /* silent */ }
  };

  const arch = report?.archetype?.primary;
  const cell = report?.cell || null;
  const archBare = (arch?.name || "").toLowerCase().replace(/^the\s+/, "");
  const notePills = [
    { label: "A small win today", text: "A small win today was " },
    { label: "What drained me", text: "What drained me today was " },
    { label: "I'm curious about", text: "Lately I've been curious about " },
    { label: "How my direction feels", text: "Right now my direction feels " },
  ];
  if (archBare) {
    notePills.unshift({ label: `The ${archBare} in me`, text: `The ${archBare} in me showed up today when ` });
  }
  const hello = buildHello({
    name,
    archName: arch?.name,
    streakDays: streak.streak_days,
    now: new Date(),
  });

  return (
    <div className="relative max-w-5xl mx-auto px-5 pt-8 pb-24">
      {/* Daily check-in quick-log popup. Shows once a day when nothing has
          been logged today and the person has not dismissed it. */}
      <CheckinPopup
        lastNoteDay={notes[0]?.day || null}
        ready={resolved && notesLoaded}
        onSaved={() => {
          loadNotes();
          client.get("/engagement/streak").then((r) => setStreak(r.data || streak)).catch(() => {});
        }}
      />

      {/* Warm greeting card. Renders only when the endpoint returns a
          non-null greeting, i.e. second-or-later visit with an active
          pledge. Never surfaces the check-in state; the copy is the
          same open door for every state. */}
      {greeting && <ReturnRitualCard greeting={greeting} base={base} />}

      {college && <CollegeWelcomeCard college={college} base={base} />}

      <div
        className="k-fade-in"
        style={{ opacity: resolved ? 1 : 0, transition: "opacity 320ms ease" }}
      >
        <div className="k-label mb-2" data-testid="home-kicker">
          {hello.kicker}
        </div>
        <h1 className="klareet-serif mb-2" style={{ fontSize: "clamp(2.2rem, 5vw, 3.4rem)", lineHeight: 1.04, letterSpacing: "-0.02em" }} data-testid="home-title">
          {hello.title}
        </h1>
        {report?.honest_line && (
          <p className="k-hero-sub" data-testid="home-honest-line">{report.honest_line}</p>
        )}
      </div>

      <Tabs defaultValue="overview" className="mt-8" data-testid="home-tabs">
        <TabsList className="flex flex-wrap gap-2 bg-transparent p-0 h-auto justify-start mb-6" data-testid="home-tabs-list">
          <TabsTrigger value="overview" className={TAB_CLS} data-testid="tab-overview">Overview</TabsTrigger>
          <TabsTrigger value="journal" className={TAB_CLS} data-testid="tab-journal">Journal</TabsTrigger>
          <TabsTrigger value="goals" className={TAB_CLS} data-testid="tab-goals">My Goals</TabsTrigger>
          <TabsTrigger value="rhythm" className={TAB_CLS} data-testid="tab-rhythm">Rhythm &amp; Check-in</TabsTrigger>
        </TabsList>

        <TabsContent value="overview" className="space-y-6 focus-visible:outline-none">
      <DailyQuoteCard arch={arch} mood={notes[0]?.mood || null} />
      <WeeklyPulseCard product={product} base={base} />
      <WeeklyDigest base={base} />
      <div className="grid sm:grid-cols-3 gap-5">
        <StatCard
          testid="stat-streak"
          tint="flame"
          icon={<Flame size={16} />}
          value={String(streak.streak_days)}
          label="day streak"
          hint={`${streak.last_days_logged} ${streak.last_days_logged === 1 ? "day" : "days"} you've checked in`}
          info="How many days in a row you've stopped by. Small and steady beats big and rare."
        />
        <StatCard
          testid="stat-archetype"
          serif
          tint="violet"
          icon={<Sparkles size={16} />}
          value={arch?.name || "your pattern"}
          label="your pattern"
          hint={arch?.tag ? `leans ${arch.tag.toLowerCase()}` : ""}
          info="The pattern your answers matched most closely. It's a starting point for the conversation, not a box you're stuck in."
          code={arch?.code}
        />
        <StatCard
          testid="stat-fit"
          serif
          tint="teal"
          icon={<Compass size={16} />}
          value={cell?.quadrant_or_cell || "your standing"}
          label="where you stand"
          hint={fitHint(cell)}
          info="Fit is how clearly one direction pulls ahead of the rest in your answers. Readiness is how set up you are to act on it right now. Both grow as you check in."
        />
      </div>

      {/* Jump into: a compact bento so the row stays balanced, not empty. */}
      <div>
        <div className="k-label mb-3">jump into</div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4" data-testid="jump-into-bento">
          <BentoTile to={`${base}/checkin`} icon={<Sparkles size={18} />} label="Check in with Klair" accent testid="jump-checkin" />
          <BentoTile to={`${base}/report`} icon={<Compass size={18} />} label="My Snapshot" tone="violet" testid="jump-report" />
          <BentoTile to={`${base}/roadmap`} icon={<Map size={18} />} label="Roadmap" tone="flame" testid="jump-roadmap" />
          <BentoTile to={`${base}/learning`} icon={<BookOpen size={18} />} label="Learning" tone="sky" testid="jump-learning" />
        </div>
      </div>

      <MovementCard movement={movement} />
      <RoadmapNudges base={base} />
      <UpcomingCard items={upcoming} base={base} />
      <ReadingRailCard entries={rail} onSetState={setRailState} onRemove={removeRail} />
        </TabsContent>

        <TabsContent value="journal" className="focus-visible:outline-none" data-testid="journal-tab">
          <NoteNudge lastNoteDay={notes[0]?.day || null} onWrite={() => { const el = document.querySelector('[data-testid="reflect-text"]'); if (el) el.focus(); }} />
          <div className="k-card">
          <div className="k-label mb-2 flex items-center gap-2"><NotebookPen size={13} /> a note to yourself today</div>
          <h3 className="klareet-serif mb-1" style={{ fontSize: "1.25rem" }}>What is on your mind?</h3>
          <p className="k-quiet !text-xs mb-3">Klair reads your latest note when you check in, so it can pick up where your head is at.</p>
          <div className="flex flex-wrap gap-2 mb-3" data-testid="note-pills">
            {notePills.map((p, i) => (
              <button
                key={i}
                type="button"
                onClick={() => setNote(p.text)}
                className="k-pill !py-1.5 !px-3 !text-xs"
                data-testid={`note-pill-${i}`}
              >
                {p.label}
              </button>
            ))}
          </div>
          <textarea
            className="k-input klareet-serif"
            rows={3}
            placeholder="one honest sentence is enough"
            value={note}
            maxLength={800}
            onChange={(e) => setNote(e.target.value)}
            data-testid="reflect-text"
          />
          <div className="mt-3">
            <div className="k-quiet !text-xs mb-1.5">how did today feel? (optional)</div>
            <MoodPicker value={mood} onChange={setMood} testid="reflect-mood" />
          </div>
          <label className="flex items-center gap-2.5 mt-3 cursor-pointer select-none w-fit" data-testid="reflect-make-goal">
            <span
              className="w-9 h-5 rounded-full relative transition-colors shrink-0"
              style={{ background: makeGoal ? "hsl(var(--klareet-teal))" : "hsl(var(--klareet-line))" }}
            >
              <span className="absolute top-0.5 w-4 h-4 rounded-full bg-white transition-all" style={{ left: makeGoal ? "18px" : "2px" }} />
            </span>
            <input type="checkbox" className="sr-only" checked={makeGoal} onChange={(e) => setMakeGoal(e.target.checked)} data-testid="reflect-make-goal-input" />
            <span className="text-sm flex items-center gap-1.5" style={{ color: "hsl(var(--klareet-ink))" }}>
              <Target size={13} /> Make this a goal
            </span>
          </label>
          <div className="flex justify-between items-center mt-3">
            <span className="k-quiet !text-xs flex items-center gap-1.5"><ShieldCheck size={12} /> private to you</span>
            <button onClick={reflect} disabled={!note.trim() || busy} className="k-btn k-btn-primary" data-testid="reflect-submit">
              {busy ? "Saving." : "Save note"}
            </button>
          </div>

          {reflectResult && !reflectResult.error && (
            <div className="k-chip k-chip-teal mt-3" data-testid="reflect-ok">
              <Check size={12} /> saved{goalAdded ? ", added to your goals" : ""}
            </div>
          )}
          {reflectResult?.error && (
            <div className="k-chip k-chip-warn mt-3" data-testid="reflect-error">{reflectResult.error}</div>
          )}
          {reflectResult && !reflectResult.error && reflectResult.looks_like_goal && !goalAdded && (
            <div
              className="mt-3 rounded-xl p-3 flex items-start gap-2.5"
              style={{ background: "hsl(var(--klareet-teal) / 0.08)", border: "1px solid hsl(var(--klareet-teal) / 0.22)" }}
              data-testid="goal-suggestion"
            >
              <Target size={15} className="shrink-0 mt-0.5" style={{ color: "hsl(var(--klareet-teal))" }} />
              <div className="flex-1 min-w-0">
                <div className="text-sm" style={{ color: "hsl(var(--klareet-ink))" }}>That reads like a goal. Want Klair to add it to your goals?</div>
                <button onClick={() => addNoteAsGoal(reflectResult.text)} className="k-btn k-btn-ghost !py-1 !px-2 !text-xs mt-2" data-testid="goal-suggestion-add">
                  <Plus size={12} /> Add to my goals
                </button>
              </div>
            </div>
          )}
          {reflectResult?.support_notice && (
            <div className="k-warm-card mt-3 p-3 text-sm" data-testid="reflect-support">
              {reflectResult.support_notice}
            </div>
          )}
        </div>

          {notes.length > 0 && (
            <div className="mt-6" data-testid="recent-notes">
              <div className="flex items-center justify-between mb-3">
                <div className="k-label !mb-0">recent notes</div>
                <Link to={`${base}/journal`} className="k-quiet !text-xs flex items-center gap-1 hover:opacity-80" data-testid="recent-notes-seeall">
                  see all <ArrowRight size={11} />
                </Link>
              </div>
              <div className="grid sm:grid-cols-2 gap-4">
                {notes.slice(0, 6).map((n, i) => {
                  const t = (n.mood && moodTint(n.mood)) || noteTint(n.id);
                  return (
                    <div
                      key={n.id}
                      className="relative rounded-2xl p-4 overflow-hidden k-fade-in transition-all duration-300 hover:-translate-y-0.5 hover:shadow-md"
                      style={{ background: t.bg, border: `1px solid ${t.border}`, animationDelay: `${i * 40}ms` }}
                      data-testid={`recent-note-${n.id}`}
                    >
                      <div className="absolute left-0 top-0 bottom-0 w-1" style={{ background: t.bar }} />
                      <div className="klareet-serif pl-2" style={{ color: "hsl(var(--klareet-ink))", lineHeight: 1.5, fontSize: "1.02rem" }}>{n.text}</div>
                      <div className="k-quiet !text-xs mt-2 pl-2 flex items-center gap-2">
                        <span>{formatNoteDay(n.day)}</span>
                        {n.mood && (<><span>·</span><MoodTag mood={n.mood} /></>)}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </TabsContent>

        <TabsContent value="goals" className="focus-visible:outline-none" data-testid="goals-tab">
          <GoalsTab base={base} />
        </TabsContent>

        <TabsContent value="rhythm" className="space-y-6 focus-visible:outline-none" data-testid="rhythm-tab">
          <RhythmPanel base={base} />
          <PledgeCard product={product} />
        </TabsContent>
      </Tabs>
    </div>
  );
}


const TAB_CLS = "px-4 py-2 rounded-full text-sm font-medium border transition-colors border-[hsl(var(--klareet-line-soft))] text-[hsl(var(--klareet-ink-soft))] hover:bg-[hsl(var(--klareet-canvas-2))] data-[state=active]:bg-[hsl(var(--klareet-ink))] data-[state=active]:text-white data-[state=active]:border-[hsl(var(--klareet-ink))]";


function DailyQuoteCard({ arch, mood }) {
  // KLA-041: the line leans into their archetype and their latest
  // logged mood. A rough day gets the gentle pool.
  const { line, tuned } = personalQuoteOfDay({
    arch,
    mood,
    familyQuotes: arch ? FAMILY_QUOTES[familyOf(arch)] : null,
  });
  return (
    <div
      className="rounded-2xl p-5 flex items-start gap-3 k-fade-in"
      style={{
        background: "linear-gradient(135deg, hsl(var(--klareet-teal) / 0.10), hsl(var(--klareet-canvas-2)))",
        border: "1px solid hsl(var(--klareet-teal) / 0.22)",
      }}
      data-testid="daily-quote-card"
    >
      <span
        className="w-9 h-9 rounded-xl flex items-center justify-center shrink-0"
        style={{ background: "hsl(var(--klareet-teal) / 0.15)", color: "hsl(var(--klareet-teal))" }}
      >
        <Quote size={17} />
      </span>
      <div className="min-w-0">
        <div className="k-label mb-1">a line for today{tuned ? `, ${tuned}` : ""}</div>
        <p className="klareet-serif" style={{ fontSize: "1.15rem", color: "hsl(var(--klareet-ink))", lineHeight: 1.45 }} data-testid="daily-quote-text">
          {line}
        </p>
      </div>
    </div>
  );
}


function BentoTile({ to, icon, label, accent, tone, testid }) {
  const t = (!accent && tone && TINTS[tone]) || null;
  return (
    <Link
      to={to}
      data-testid={testid}
      className="group rounded-2xl p-4 flex flex-col gap-3 transition-all duration-300 hover:-translate-y-1 hover:shadow-md"
      style={{
        background: accent ? "hsl(var(--klareet-teal))" : t ? t.bg : "hsl(var(--klareet-canvas-2))",
        border: `1px solid ${accent ? "hsl(var(--klareet-teal))" : t ? t.border : "hsl(var(--klareet-line-soft))"}`,
        color: accent ? "#fff" : "hsl(var(--klareet-ink))",
        minHeight: 108,
      }}
    >
      <span
        className="w-9 h-9 rounded-xl flex items-center justify-center transition-transform duration-300 group-hover:scale-110"
        style={{
          background: accent ? "rgba(255,255,255,0.18)" : t ? t.iconBg : "hsl(var(--klareet-teal) / 0.12)",
          color: accent ? "#fff" : t ? t.iconColor : "hsl(var(--klareet-teal))",
        }}
      >
        {icon}
      </span>
      <span className="text-sm font-semibold leading-tight">{label}</span>
    </Link>
  );
}


function isoWeekKey(d = new Date()) {
  const date = new Date(Date.UTC(d.getFullYear(), d.getMonth(), d.getDate()));
  const dayNum = (date.getUTCDay() + 6) % 7;
  date.setUTCDate(date.getUTCDate() - dayNum + 3);
  const firstThursday = new Date(Date.UTC(date.getUTCFullYear(), 0, 4));
  const week = 1 + Math.round(((date - firstThursday) / 86400000 - 3 + ((firstThursday.getUTCDay() + 6) % 7)) / 7);
  return `${date.getUTCFullYear()}-W${String(week).padStart(2, "0")}`;
}

const RHYTHM_MOVES = [
  { key: "step", icon: Rocket, title: "Step up", body: "Take one small, visible step toward a goal. The smallest one you could not talk yourself out of." },
  { key: "share", icon: Share2, title: "Share it", body: "Tell one person what you did. Saying it out loud makes it real and keeps you honest." },
  { key: "check", icon: MessageCircle, title: "Check in", body: "Come back and tell Klair how it landed. That is how your snapshot stays alive and keeps up with you." },
];

function RhythmPanel({ base }) {
  const weekKey = isoWeekKey();
  const storageKey = `klareet.rhythm.${weekKey}`;
  const advancedKey = `klareet.rhythm.${weekKey}.stepAdvanced`;
  const [done, setDone] = useState({});
  const [focus, setFocus] = useState(null);
  const [streakMsg, setStreakMsg] = useState("");

  useEffect(() => {
    try {
      const raw = localStorage.getItem(storageKey);
      setDone(raw ? JSON.parse(raw) : {});
    } catch (_e) { setDone({}); }
  }, [storageKey]);

  const loadFocus = () => {
    client.get("/engagement/roadmap/focus")
      .then((r) => setFocus(r?.data?.has_focus ? r.data : null))
      .catch(() => setFocus(null));
  };
  useEffect(() => { loadFocus(); }, []);

  const advanceRiver = async () => {
    if (!focus || !focus.next_subtask) return;
    if (localStorage.getItem(advancedKey) === "1") return; // once per week
    try {
      await client.post(`/engagement/goal/${focus.goal_id}/roadmap/phase/${focus.phase_id}/subtask/${focus.next_subtask.id}`, { done: true });
      localStorage.setItem(advancedKey, "1");
      setStreakMsg(`Nice. That moved "${focus.goal_title}" forward a step.`);
      loadFocus();
    } catch (_e) { /* silent */ }
  };

  const toggle = (key) => {
    setDone((prev) => {
      const turningOn = !prev[key];
      const next = { ...prev, [key]: !prev[key] };
      try { localStorage.setItem(storageKey, JSON.stringify(next)); } catch (_e) { /* ignore */ }
      if (key === "step" && turningOn) advanceRiver();
      return next;
    });
  };

  const doneCount = RHYTHM_MOVES.filter((m) => done[m.key]).length;
  const pillars = [
    { tag: "why", icon: Lightbulb, title: "Why it is here", body: "A light rhythm keeps your snapshot alive. Small, steady check-ins beat one big push you never repeat." },
    { tag: "what", icon: RotateCcw, title: "What it means", body: "Every week or so, Klair asks how things landed. Nothing here is graded, and nobody else ever sees it." },
    { tag: "do", icon: Compass, title: "What to do", body: "Take one small step, tell someone, then tell Klair. Three light moves, that is the whole loop." },
  ];

  return (
    <>
      <div className="k-card" data-testid="rhythm-panel">
        <div className="k-label mb-1">your rhythm</div>
        <h2 className="klareet-serif mb-2" style={{ fontSize: "clamp(1.6rem, 3vw, 2.1rem)", letterSpacing: "-0.01em" }}>
          Welcome to your rhythm.
        </h2>
        <p className="k-quiet !text-sm mb-6" style={{ maxWidth: 620, lineHeight: 1.55 }}>
          This is the loop that keeps your snapshot honest over time. It is small on purpose, so it fits
          a real life and not just a good week.
        </p>
        <div className="grid sm:grid-cols-3 gap-4">
          {pillars.map((p) => {
            const Icon = p.icon;
            return (
              <div
                key={p.tag}
                className="rounded-2xl p-5 transition-all duration-300 hover:-translate-y-1 hover:shadow-md"
                style={{ background: "hsl(var(--klareet-canvas-2))", border: "1px solid hsl(var(--klareet-line-soft))" }}
                data-testid={`rhythm-${p.tag}`}
              >
                <span className="w-9 h-9 rounded-xl flex items-center justify-center mb-3" style={{ background: "hsl(var(--klareet-teal) / 0.12)", color: "hsl(var(--klareet-teal))" }}>
                  <Icon size={17} />
                </span>
                <div className="font-semibold mb-1" style={{ color: "hsl(var(--klareet-ink))" }}>{p.title}</div>
                <div className="k-quiet !text-xs" style={{ lineHeight: 1.55 }}>{p.body}</div>
              </div>
            );
          })}
        </div>
      </div>

      <div className="k-card mt-6" data-testid="rhythm-moves-card">
        <div className="flex items-center justify-between mb-1 flex-wrap gap-2">
          <div>
            <div className="k-label mb-1">this week's moves</div>
            <h3 className="klareet-serif" style={{ fontSize: "1.3rem" }}>Three light moves, one week.</h3>
          </div>
          <span className="k-chip" data-testid="rhythm-progress">
            <CheckCircle2 size={12} /> {doneCount} of {RHYTHM_MOVES.length} done
          </span>
        </div>
        <p className="k-quiet !text-xs mb-4">Tick these off as you go. They reset with each new week, so it is a fresh, easy start every time.</p>
        {focus && focus.next_subtask && (
          <Link
            to={`${base}/roadmap?goal=${focus.goal_id}`}
            className="flex items-center gap-2.5 rounded-xl p-3 mb-4 transition-all hover:-translate-y-0.5"
            style={{ background: "hsl(var(--klareet-teal) / 0.07)", border: "1px solid hsl(var(--klareet-teal) / 0.22)" }}
            data-testid="rhythm-river-link"
          >
            <Rocket size={15} className="shrink-0" style={{ color: "hsl(var(--klareet-teal))" }} />
            <div className="min-w-0 flex-1">
              <div className="text-sm font-medium truncate" style={{ color: "hsl(var(--klareet-ink))" }}>Ticking Step up moves your river forward</div>
              <div className="k-quiet !text-xs truncate">{focus.goal_title} · {focus.phase_title}</div>
            </div>
            <ArrowRight size={14} className="shrink-0 opacity-50" />
          </Link>
        )}
        {streakMsg && (
          <div className="k-chip k-chip-teal mb-4" data-testid="rhythm-streak-msg"><Check size={11} /> {streakMsg}</div>
        )}
        <div className="space-y-3">
          {RHYTHM_MOVES.map((m) => {
            const Icon = m.icon;
            const on = !!done[m.key];
            return (
              <button
                key={m.key}
                type="button"
                onClick={() => toggle(m.key)}
                data-testid={`rhythm-move-${m.key}`}
                className="w-full text-left flex items-start gap-3 rounded-xl p-4 transition-all duration-200 hover:-translate-y-0.5"
                style={{
                  background: on ? "hsl(var(--klareet-teal) / 0.08)" : "hsl(var(--klareet-canvas-2))",
                  border: `1px solid ${on ? "hsl(var(--klareet-teal) / 0.3)" : "hsl(var(--klareet-line-soft))"}`,
                }}
              >
                <span className="w-9 h-9 rounded-xl flex items-center justify-center shrink-0" style={{ background: "hsl(var(--klareet-teal) / 0.12)", color: "hsl(var(--klareet-teal))" }}>
                  <Icon size={16} />
                </span>
                <div className="flex-1 min-w-0">
                  <div className="font-semibold flex items-center gap-2" style={{ color: "hsl(var(--klareet-ink))" }}>
                    {m.title}
                    {on && <span className="k-chip k-chip-teal !text-[10px]"><Check size={10} /> done</span>}
                  </div>
                  <div className="k-quiet !text-xs mt-0.5" style={{ lineHeight: 1.5 }}>{m.body}</div>
                </div>
                {on
                  ? <CheckCircle2 size={20} className="shrink-0 mt-0.5" style={{ color: "hsl(var(--klareet-teal))" }} />
                  : <Circle size={20} className="shrink-0 mt-0.5" style={{ color: "hsl(var(--klareet-muted))" }} />}
              </button>
            );
          })}
        </div>
        <Link to={`${base}/checkin`} className="k-btn k-btn-primary mt-5 !text-sm" data-testid="rhythm-checkin"><Sparkles size={14} /> Check in with Klair</Link>
      </div>
    </>
  );
}


function ReadingRailCard({ entries, onSetState, onRemove }) {
  if (!entries || entries.length === 0) return null;
  return (
    <div className="k-card mt-5" data-testid="reading-rail-card">
      <div className="k-label mb-3 flex items-center gap-2">
        <Bookmark size={14} /> your reading rail
        <span className="k-quiet !text-xs ml-2">{entries.length} saved</span>
      </div>
      <ul className="space-y-2">
        {entries.map((e) => (
          <li
            key={e.id}
            className="flex items-start gap-3 rounded-xl p-3"
            style={{
              background: "hsl(var(--klareet-canvas-2))",
              border: "1px solid hsl(var(--klareet-line-soft))",
            }}
            data-testid={`rail-${e.id}`}
          >
            <RailCover entry={e} />
            <div className="min-w-0 flex-1">
              <div className="flex items-baseline gap-2 flex-wrap">
                <span
                  className="font-semibold"
                  style={{ color: "hsl(var(--klareet-ink))" }}
                  data-testid={`rail-${e.id}-title`}
                >
                  {e.title}
                </span>
                <span className="k-quiet !text-xs">by {e.author}</span>
                {e.anchor && (
                  <span
                    className="klareet-mono !text-[10px] px-2 py-0.5 rounded-full"
                    style={{
                      color: "hsl(var(--klareet-muted))",
                      background: "hsl(var(--card))",
                      border: "1px solid hsl(var(--klareet-line-soft))",
                    }}
                  >
                    {e.anchor.replaceAll("_", " ")}
                  </span>
                )}
              </div>
              {e.why && (
                <div className="k-quiet !text-sm mt-1">{e.why}</div>
              )}
              <div className="mt-2 flex items-center gap-1 flex-wrap">
                {["want_to_read", "reading", "read"].map((s) => (
                  <button
                    key={s}
                    type="button"
                    onClick={() => onSetState(e.id, s)}
                    className={`klareet-mono !text-[10px] px-2 py-0.5 rounded-full transition-colors ${
                      e.state === s ? "" : "opacity-60 hover:opacity-100"
                    }`}
                    style={{
                      color: e.state === s ? "hsl(140 40% 30%)" : "hsl(var(--klareet-muted))",
                      background: e.state === s ? "hsl(140 40% 95%)" : "hsl(var(--card))",
                      border: e.state === s ? "1px solid hsl(140 40% 75%)" : "1px solid hsl(var(--klareet-line-soft))",
                    }}
                    data-testid={`rail-${e.id}-state-${s}`}
                    aria-pressed={e.state === s}
                  >
                    {s.replaceAll("_", " ")}
                  </button>
                ))}
              </div>
            </div>
            <button
              type="button"
              onClick={() => onRemove(e.id)}
              className="p-1 rounded-full opacity-60 hover:opacity-100 shrink-0"
              data-testid={`rail-${e.id}-remove`}
              aria-label={`Remove ${e.title} from reading rail`}
              title="Remove from reading rail"
              style={{ color: "hsl(var(--klareet-muted))" }}
            >
              <XIcon size={14} />
            </button>
          </li>
        ))}
      </ul>
    </div>
  );
}


function RailCover({ entry }) {
  const [failed, setFailed] = useState(false);
  // Open Library ISBN endpoint. The rail row stores the source
  // book id but not the ISBN; ISBN is derived server-side on
  // report time. Fall back to a plate whenever the image fails.
  const size = { w: 42, h: 62 };
  const style = {
    width: size.w,
    height: size.h,
    borderRadius: 6,
    boxShadow: "0 2px 6px hsl(var(--klareet-ink) / 0.08)",
    background: "hsl(var(--klareet-canvas-2))",
    border: "1px solid hsl(var(--klareet-line-soft))",
    overflow: "hidden",
    flexShrink: 0,
  };
  const initials = (entry.title || "?")
    .split(" ").filter(Boolean).slice(0, 2)
    .map((w) => w[0]).join("").toUpperCase();
  const url = entry.cover_url || "";
  if (url && !failed) {
    return (
      <img
        src={url}
        alt={`${entry.title} cover`}
        loading="lazy"
        onError={() => setFailed(true)}
        style={{ ...style, objectFit: "cover" }}
      />
    );
  }
  return (
    <div style={{
      ...style, display: "flex", alignItems: "center", justifyContent: "center",
    }}>
      <span
        className="klareet-serif"
        style={{ color: "hsl(var(--klareet-muted))", fontSize: 12, letterSpacing: "0.06em" }}
      >
        {initials}
      </span>
    </div>
  );
}


function CollegeWelcomeCard({ college, base }) {
  const seenKey = "klareet.college.welcome.seen";
  const [show, setShow] = useState(() => {
    try { return !localStorage.getItem(seenKey); } catch (_e) { return true; }
  });
  if (!show || !college) return null;
  const name = college.display_name || "your college";
  const firstLine = (college.about || "").split(/(?<=[.!?])\s/)[0] || "";
  const dismiss = () => {
    try { localStorage.setItem(seenKey, "1"); } catch (_e) { /* ignore */ }
    setShow(false);
  };
  return (
    <div
      className="rounded-2xl p-5 mb-5 k-fade-in"
      style={{ background: "hsl(var(--klareet-teal) / 0.06)", border: "1px solid hsl(var(--klareet-teal) / 0.22)" }}
      data-testid="college-welcome-card"
    >
      <div className="flex items-start gap-3">
        {college.logo_url ? (
          <img
            src={college.logo_url}
            alt={name}
            className="w-10 h-10 rounded-xl object-contain shrink-0 bg-white p-1"
            style={{ border: "1px solid hsl(var(--klareet-teal) / 0.25)" }}
          />
        ) : (
          <div className="w-10 h-10 rounded-xl flex items-center justify-center shrink-0" style={{ background: "hsl(var(--klareet-teal))", color: "white" }}>
            <GraduationCap size={18} />
          </div>
        )}
        <div className="flex-1 min-w-0">
          <div className="k-label mb-1">a hello from your college</div>
          <div className="klareet-serif" style={{ fontSize: "1.05rem", color: "hsl(var(--klareet-ink))", lineHeight: 1.5 }} data-testid="college-welcome-line">
            Welcome from {name}. {firstLine}
          </div>
        </div>
        <button onClick={dismiss} className="p-1 rounded-full opacity-60 hover:opacity-100 shrink-0" aria-label="dismiss" data-testid="college-welcome-dismiss" style={{ color: "hsl(var(--klareet-muted))" }}>
          <XIcon size={14} />
        </button>
      </div>
      <div className="mt-3 flex flex-wrap items-center gap-x-4 gap-y-2 sm:pl-[52px]">
        <Link to={`${base}/college`} className="k-btn k-btn-ghost !py-1 !px-2 !text-xs" data-testid="college-welcome-link">
          More about {name} <Compass size={12} />
        </Link>
        <span className="k-quiet !text-xs flex items-start gap-1.5" style={{ lineHeight: 1.4, maxWidth: 360 }}>
          <ShieldCheck size={13} className="shrink-0 mt-0.5" style={{ color: "hsl(var(--klareet-teal))" }} />
          They set Klareet up for you. It stays private, they never see your answers.
        </span>
      </div>
    </div>
  );
}


function UpcomingCard({ items, base }) {
  if (!items || items.length === 0) return null;
  return (
    <div className="k-card mt-5" data-testid="upcoming-card">
      <div className="k-label mb-3 flex items-center gap-2"><Bell size={14} /> coming up</div>
      <ul className="space-y-2">
        {items.map((it) => (
          <li
            key={it.key}
            className="flex items-center gap-3 rounded-xl p-3"
            style={{ background: "hsl(var(--klareet-canvas-2))", border: "1px solid hsl(var(--klareet-line-soft))" }}
            data-testid={`upcoming-${it.key}`}
          >
            <span
              className="w-7 h-7 rounded-full flex items-center justify-center shrink-0"
              style={{
                background: it.overdue ? "hsl(var(--klareet-amber) / 0.15)" : "hsl(var(--klareet-teal) / 0.15)",
                color: it.overdue ? "hsl(var(--klareet-amber))" : "hsl(var(--klareet-teal))",
              }}
            >
              {it.overdue ? <AlertCircle size={14} /> : <Bell size={14} />}
            </span>
            <div className="min-w-0 flex-1">
              <div className="font-medium truncate" style={{ color: "hsl(var(--klareet-ink))" }} data-testid={`upcoming-${it.key}-label`}>
                {it.label}
              </div>
              <div className="k-quiet !text-xs" data-testid={`upcoming-${it.key}-date`}>
                {it.overdue ? `was due ${it.remind_on}` : `remind me ${it.remind_on}`}
                {it.status === "in_progress" ? " · in progress" : ""}
              </div>
            </div>
            <Link to={`${base}/roadmap`} className="k-btn k-btn-ghost !py-1 !px-2 !text-xs shrink-0" data-testid={`upcoming-${it.key}-open`}>
              Open
            </Link>
          </li>
        ))}
      </ul>
    </div>
  );
}


function ReturnRitualCard({ greeting, base }) {
  const goal = (greeting.goal_text || "").trim();
  const cite = greeting.cite;
  const movedUp = (cite?.direction || "").includes("up");
  return (
    <div
      className="rounded-2xl p-5 mb-5 k-fade-in"
      style={{
        background: "hsl(var(--klareet-canvas-2))",
        border: "1px solid hsl(var(--klareet-line-soft))",
      }}
      data-testid="return-ritual-card"
    >
      <div className="flex items-start gap-3">
        <div
          className="w-10 h-10 rounded-xl flex items-center justify-center shrink-0"
          style={{ background: "hsl(var(--klareet-teal) / 0.12)", color: "hsl(var(--klareet-teal))" }}
        >
          <RotateCcw size={18} />
        </div>
        <div className="flex-1 min-w-0">
          <div className="k-label mb-1">welcome back</div>
          <div
            className="klareet-serif"
            style={{ fontSize: "1.15rem", color: "hsl(var(--klareet-ink))", lineHeight: 1.4 }}
            data-testid="return-ritual-primary"
          >
            {greeting.primary}
          </div>
        </div>
      </div>

      {goal && (
        <div
          className="mt-4 rounded-xl p-3"
          style={{ background: "hsl(var(--klareet-canvas))", border: "1px solid hsl(var(--klareet-line-soft))" }}
          data-testid="return-ritual-goal"
        >
          <div className="k-label mb-1 flex items-center gap-1.5"><Target size={12} /> the goal you set</div>
          <div className="text-sm" style={{ color: "hsl(var(--klareet-ink))", lineHeight: 1.5 }}>
            {goal}
          </div>
        </div>
      )}

      {cite?.text && (
        <div className="mt-3 flex items-start gap-2" data-testid="return-ritual-cite">
          <span
            className="inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs shrink-0 mt-0.5"
            style={{
              background: movedUp ? "hsl(var(--klareet-teal) / 0.12)" : "hsl(var(--klareet-amber) / 0.12)",
              color: movedUp ? "hsl(var(--klareet-teal))" : "hsl(var(--klareet-amber))",
            }}
          >
            {movedUp ? <TrendingUp size={12} /> : <TrendingDown size={12} />}
            {movedUp ? "up a little" : "down a little"}
          </span>
          <div className="k-quiet !text-xs" style={{ lineHeight: 1.5 }}>
            {cite.text}
            {cite.trial_note && <span className="ml-1">{cite.trial_note}</span>}
          </div>
        </div>
      )}

      <div className="mt-4 flex flex-wrap items-center gap-3">
        <Link
          to={`${base}/checkin`}
          className="k-btn k-btn-primary !py-1.5 !px-3 !text-sm"
          data-testid="return-ritual-continue"
        >
          Pick up where I left off <Compass size={14} />
        </Link>
        <span className="k-quiet !text-xs flex items-start gap-1.5" style={{ lineHeight: 1.4, maxWidth: 340 }}>
          <HelpCircle size={13} className="shrink-0 mt-0.5" />
          Just a warm hello. Your spot is saved, so come back whenever. No pressure, no catching up.
        </span>
      </div>
    </div>
  );
}


function MovementCard({ movement }) {
  const view = movement?.view || "empty";
  const rows = movement?.rows || [];
  const count = movement?.read_count || 0;
  const mover = movement?.biggest_mover || null;
  const honesty = movement?.honesty || {};

  return (
    <div className="k-card mt-8" data-testid="movement-card">
      <div className="flex items-center justify-between mb-2">
        <div>
          <div className="k-label mb-1">your movement</div>
          <h3 className="klareet-serif" style={{ fontSize: "1.25rem" }} data-testid="movement-title">
            {view === "bars" && "How each side of you has changed between your two reads."}
            {view === "line" && "How each side of you has changed across your reads."}
            {view === "empty" && "Your movement will show up after your second check in."}
          </h3>
          <p className="k-quiet !text-sm mt-1" data-testid="movement-explainer">
            This is the gap between your first read and your latest one, side by side. It shows change over time, never a score or a ranking.
          </p>
        </div>
        <div className="k-chip">
          <Activity size={12} />
          {count} {count === 1 ? "read" : "reads"}
        </div>
      </div>

      {view === "empty" && (
        <p className="k-quiet text-sm" data-testid="movement-empty">
          Come back next week and this card will draw itself.
        </p>
      )}

      {view !== "empty" && (
        <>
          {mover?.sentence && (
            <p
              className="klareet-serif mt-1 mb-4"
              style={{ color: "hsl(var(--klareet-ink))", fontSize: "1rem" }}
              data-testid="movement-mover"
            >
              {mover.sentence}
            </p>
          )}
          <div className="grid sm:grid-cols-2 gap-3">
            {rows.map((r) => (
              <MovementBarRow key={r.layer} row={r} />
            ))}
          </div>
          {/* Three honesty guards, always on. */}
          <div
            className="mt-4 rounded-lg p-3 space-y-1"
            style={{ background: "hsl(var(--klareet-canvas-2))", border: "1px solid hsl(var(--klareet-line-soft))" }}
            data-testid="movement-honesty"
          >
            {honesty.trial_wording && (
              <div className="k-quiet !text-xs" data-testid="movement-honesty-trial">{honesty.trial_wording}</div>
            )}
            {honesty.movement_not_trend && (
              <div className="k-quiet !text-xs" data-testid="movement-honesty-not-trend">{honesty.movement_not_trend}</div>
            )}
            {honesty.your_line_not_a_rank && (
              <div className="k-quiet !text-xs" data-testid="movement-honesty-not-rank">{honesty.your_line_not_a_rank}</div>
            )}
          </div>
        </>
      )}
    </div>
  );
}


function MovementBarRow({ row }) {
  const delta = typeof row.delta === "number" ? row.delta : null;
  const first = typeof row.first === "number" ? row.first : null;
  const current = typeof row.current === "number" ? row.current : null;
  const testKey = `movement-row-${row.layer}`;
  const pct = current != null
    ? Math.max(2, Math.min(100, Math.round((current / 5) * 100)))
    : 0;
  const startPct = first != null
    ? Math.max(0, Math.min(100, Math.round((first / 5) * 100)))
    : 0;
  const deltaColor = delta == null
    ? "hsl(var(--klareet-muted))"
    : delta > 0.05
    ? "hsl(var(--klareet-teal))"
    : delta < -0.05
    ? "hsl(var(--klareet-amber))"
    : "hsl(var(--klareet-muted))";

  return (
    <div
      className="rounded-xl p-3"
      style={{ background: "hsl(var(--klareet-canvas-2))", border: "1px solid hsl(var(--klareet-line-soft))" }}
      data-testid={testKey}
    >
      <div className="flex items-center justify-between mb-2">
        <div className="text-sm font-medium" style={{ color: "hsl(var(--klareet-ink))" }} data-testid={`${testKey}-label`}>
          {row.layer}
        </div>
        <div className="klareet-mono text-xs" style={{ color: deltaColor }} data-testid={`${testKey}-delta`}>
          {delta == null ? "-" : `${delta > 0 ? "+" : ""}${delta.toFixed(2)}`}
        </div>
      </div>
      <div
        className="relative h-2 rounded-full overflow-hidden"
        style={{ background: "hsl(var(--klareet-line-soft))" }}
      >
        {/* start-point marker */}
        <div
          className="absolute top-0 bottom-0"
          style={{ left: `${startPct}%`, width: 2, background: "hsl(var(--klareet-muted))" }}
          data-testid={`${testKey}-start`}
        />
        {/* current bar */}
        <div
          style={{
            width: `${pct}%`, height: "100%",
            background: deltaColor,
            transition: "width 300ms ease",
          }}
          data-testid={`${testKey}-bar`}
        />
      </div>
      <div className="k-quiet !text-xs mt-1" data-testid={`${testKey}-direction`}>
        {row.direction} · then {first?.toFixed?.(2) ?? "-"} · now {current?.toFixed?.(2) ?? "-"}
      </div>
    </div>
  );
}


// KLA-042: the dashboard's colour blocking. Each card gets its own
// warm pastel so the grid reads like a friendly board, never a form.
const TINTS = {
  flame: { bg: "hsl(24 92% 96%)", border: "hsl(24 80% 88%)", iconBg: "hsl(24 90% 90%)", iconColor: "hsl(20 85% 48%)" },
  violet: { bg: "hsl(255 50% 96%)", border: "hsl(255 38% 89%)", iconBg: "hsl(255 45% 91%)", iconColor: "hsl(255 45% 55%)" },
  teal: { bg: "hsl(var(--klareet-teal) / 0.08)", border: "hsl(var(--klareet-teal) / 0.25)", iconBg: "hsl(var(--klareet-teal) / 0.15)", iconColor: "hsl(var(--klareet-teal))" },
  sky: { bg: "hsl(203 60% 95%)", border: "hsl(203 48% 86%)", iconBg: "hsl(203 55% 89%)", iconColor: "hsl(205 60% 42%)" },
};


function InfoDot({ text, testid }) {
  if (!text) return null;
  return (
    <span
      className="inline-flex items-center align-middle ml-1.5 cursor-help"
      title={text}
      data-testid={testid}
      aria-label={text}
    >
      <HelpCircle size={13} style={{ color: "hsl(var(--klareet-muted))" }} />
    </span>
  );
}


function StatCard({ testid, icon, value, label, hint, info, serif, code, tint }) {
  const t = (tint && TINTS[tint]) || null;
  return (
    <div
      className="k-card k-lift"
      style={t ? { background: t.bg, border: `1px solid ${t.border}` } : undefined}
      data-testid={testid}
    >
      <div className="flex items-center justify-between mb-3">
        <span
          className="w-9 h-9 rounded-xl flex items-center justify-center"
          style={{
            background: t ? t.iconBg : "hsl(var(--klareet-teal) / 0.12)",
            color: t ? t.iconColor : "hsl(var(--klareet-teal))",
          }}
        >
          {icon}
        </span>
        {code && <span className="klareet-mono !text-[10px] k-quiet" data-testid={`${testid}-code`}>{code}</span>}
      </div>
      <div
        className={serif ? "klareet-serif" : "klareet-mono"}
        style={{ fontSize: serif ? "1.35rem" : "1.65rem", color: "hsl(var(--klareet-ink))", fontWeight: 500, lineHeight: 1.15 }}
        data-testid={`${testid}-value`}
      >
        {value || ""}
      </div>
      <div className="text-sm font-medium mt-1 flex items-center" style={{ color: "hsl(var(--klareet-ink))" }}>
        {label}
        <InfoDot text={info} testid={`${testid}-info`} />
      </div>
      {hint && <div className="k-quiet mt-1 !text-xs">{hint}</div>}
    </div>
  );
}
